// @@PAYMENT_INFO_v82@@ Payment Info — W9 + how the client gets paid.
// @@PAYMENT_INFO_v82_1@@ W9 locks after the client saves it (admin unlocks);
// <PaymentInfoReminder> for the portal dashboard.
// @@PAYMENT_INFO_v82_2@@ staff panel: copy buttons per field + "Copy all" per
// section. Masked values (••••1234) aren't copyable — Reveal first (audited).
// @@PAYMENT_INFO_W8_v105@@ International clients (admin toggle on the staff panel)
// file a W-8BEN / W-8BEN-E instead of a W9; payouts limited to Wire / Wise / Other.
//
// One form, two homes:
//   <PaymentInfoPortalTab clientId>  client portal tab   (/api/portal/payment-info)
//   <PaymentInfoStaffPanel clientId> client detail panel (/api/clients/:id/payment-info)
// Staff see it masked; admin/manager can Reveal (audited); admin can Edit.
// Secret fields (TIN, account #, IBAN) are write-only: the server never sends
// them back unmasked except on an audited reveal. Blank = keep what's on file.
// The server re-validates everything — these checks are only for fast feedback.
import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './paymentInfo.css';

const CLS = {
  portal: { card: 'cp-card', head: 'cp-card-head', input: 'cp-input', btn: 'cp-btn-sm', pill: 'cp-pill', pillOn: 'cp-pill cp-pill-on', table: 'cp-table' },
  staff: { card: 'hq-panel', head: 'hq-panel-head', input: 'hq-input', btn: 'hq-btn', pill: 'hq-btn hq-btn-ghost', pillOn: 'hq-btn', table: 'hq-table' },
};

const METHODS = [
  { id: 'check', label: 'Check' },
  { id: 'ach', label: 'ACH (US bank)' },
  { id: 'wire', label: 'Wire (international)' },
  { id: 'wise', label: 'Wise' },
  { id: 'other', label: 'Other' },
];
const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.id, m.label]));
const TAX_CLASSES = [
  ['individual', 'Individual / sole proprietor / single-member LLC'],
  ['c_corp', 'C corporation'], ['s_corp', 'S corporation'], ['partnership', 'Partnership'],
  ['trust_estate', 'Trust / estate'], ['llc', 'Limited liability company (LLC)'], ['other', 'Other'],
];
const US_STATES = 'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR GU VI AS MP AA AE AP'.split(' ');
const CA_PROVINCES = 'AB BC MB NB NL NS NT NU ON PE QC SK YT'.split(' ');
const COUNTRY_CODES = ('US CA MX GB IE AU NZ DE FR NL BE LU AT CH IT ES PT SE NO DK FI IS PL CZ SK HU RO BG GR HR SI EE LV LT MT CY ' +
  'JP KR CN HK TW SG MY TH PH ID VN IN PK BD LK NP AE SA QA KW BH OM IL TR EG MA ZA NG KE GH ' +
  'BR AR CL CO PE UY PY BO EC VE CR PA GT HN SV NI DO JM TT BS BB PR UA RS BA ME MK AL GE AM AZ KZ').split(' ');
let REGION_NAMES = null;
try { REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' }); } catch (e) { REGION_NAMES = null; }
const countryName = (c) => { try { return (REGION_NAMES && REGION_NAMES.of(c)) || c; } catch (e) { return c; } };
const COUNTRIES = [COUNTRY_CODES[0], ...COUNTRY_CODES.slice(1).sort((a, b) => countryName(a).localeCompare(countryName(b)))];

// @@PAYMENT_INFO_W8_v105@@ W-8 vocab (mirrors server/lib/payment-info.js).
const INTL_METHODS = ['wire', 'wise', 'other'];
const NON_US = COUNTRIES.filter((c) => c !== 'US');
const W8_FORMS = [['ben', 'An individual — Form W-8BEN'], ['ben_e', 'A company, partnership, trust or other entity — Form W-8BEN-E']];
const W8_LABEL = { ben: 'W-8BEN', ben_e: 'W-8BEN-E' };
const CH3 = [
  ['corporation', 'Corporation'], ['disregarded_entity', 'Disregarded entity'], ['partnership', 'Partnership'],
  ['simple_trust', 'Simple trust'], ['grantor_trust', 'Grantor trust'], ['complex_trust', 'Complex trust'], ['estate', 'Estate'],
  ['foreign_government', 'Foreign government / controlled entity'], ['central_bank', 'Central bank of issue'],
  ['international_org', 'International organization'], ['tax_exempt_org', 'Tax-exempt organization'], ['private_foundation', 'Private foundation'],
];
const CH4 = [
  ['active_nffe', 'Active NFFE (an operating business)'], ['passive_nffe', 'Passive NFFE'],
  ['publicly_traded_nffe', 'Publicly traded NFFE (or affiliate)'], ['excepted_nffe_startup', 'Excepted NFFE (start-up / liquidating)'],
  ['nonprofit', 'Nonprofit organization'], ['participating_ffi', 'Participating FFI'], ['reporting_model1_ffi', 'Reporting Model 1 FFI'],
  ['reporting_model2_ffi', 'Reporting Model 2 FFI'], ['registered_deemed_compliant_ffi', 'Registered deemed-compliant FFI'],
  ['sponsored_ffi', 'Sponsored FFI'], ['exempt_beneficial_owner', 'Exempt beneficial owner'], ['other', 'Other (describe)'],
];
const GIIN_CH4 = ['participating_ffi', 'reporting_model2_ffi', 'registered_deemed_compliant_ffi'];
const W8_THIS_FORM = 'This form relates to: (a) income not effectively connected with the conduct of a trade or business in the United States; (b) income effectively connected with the conduct of a trade or business in the United States but not subject to tax under an applicable income tax treaty; (c) the partner’s share of a partnership’s effectively connected taxable income; or (d) the partner’s amount realized from the transfer of a partnership interest subject to withholding under section 1446(f)';
const W8_CERT = {
  ben: 'Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that: (1) I am the individual that is the beneficial owner (or am authorized to sign for the individual that is the beneficial owner) of all the income or proceeds to which this form relates or am using this form to document myself for chapter 4 purposes; (2) the person named on this form is not a U.S. person; (3) ' + W8_THIS_FORM + '; (4) the person named on this form is a resident of the treaty country listed on this form (if any) within the meaning of the income tax treaty between the United States and that country; and (5) for broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions. Furthermore, I authorize this form to be provided to any withholding agent that has control, receipt, or custody of the income of which I am the beneficial owner or any withholding agent that can disburse or make payments of the income of which I am the beneficial owner. I agree that I will submit a new form within 30 days if any certification made on this form becomes incorrect.',
  ben_e: 'Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that: (1) the entity identified on this form is the beneficial owner of all the income or proceeds to which this form relates, is using this form to certify its status for chapter 4 purposes, or is submitting this form for purposes of section 6050W or 6050Y; (2) the entity identified on this form is not a U.S. person; (3) ' + W8_THIS_FORM + '; and (4) for broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions. Furthermore, I authorize this form to be provided to any withholding agent that has control, receipt, or custody of the income of which the entity is the beneficial owner or any withholding agent that can disburse or make payments of the income of which the entity is the beneficial owner. I agree that I will submit a new form within 30 days if any certification on this form becomes incorrect. I certify that I have the capacity to sign for the entity identified on this form.',
};
const AUDIT_LABEL = {
  reveal: 'Revealed', update: 'Updated', unlock: 'Unlocked', lock: 'Locked',
  change_request: 'Change requested', international: 'Tax status',
  access: 'Portal access', // @@PAYMENT_ACCESS_v125@@
};

const W9_CERT = 'Under penalties of perjury, I certify that: (1) the number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); (2) I am not subject to backup withholding because (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; (3) I am a U.S. citizen or other U.S. person; and (4) the FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.';

const onlyDigits = (s) => String(s || '').replace(/[\s-]/g, '');
function abaValid(r) {
  if (!/^\d{9}$/.test(r)) return false;
  const d = r.split('').map(Number);
  return (3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8])) % 10 === 0;
}
const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(); } catch (e) { return '—'; } };
const emptyAddr = (country) => ({ country: country == null ? 'US' : country, line1: '', line2: '', city: '', region: '', postal: '' });
function addrLines(a) {
  if (!a) return '—';
  const cityLine = [a.city, [a.region, a.postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.line1, a.line2, cityLine, a.country !== 'US' ? countryName(a.country) : ''].filter(Boolean).join('\n');
}

async function jsonOf(res) { try { return await res.json(); } catch (e) { return {}; } }

// ---------------------------------------------------------------- inputs ----
function Field({ label, children, full, hint }) {
  return (
    <label className={full ? 'pi-field pi-full' : 'pi-field'}>
      <span className="pi-label">{label}</span>
      {children}
      {hint && <span className="pi-hint">{hint}</span>}
    </label>
  );
}

function Text({ k, value, onChange, ...rest }) {
  return <input className={k.input} value={value == null ? '' : value} onChange={(e) => onChange(e.target.value)} {...rest} />;
}

function Select({ k, value, onChange, options, placeholder }) {
  return (
    <select className={k.input} value={value || ''} onChange={(e) => onChange(e.target.value)}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function AddressFields({ k, value, onChange, lockCountry, title }) {
  const a = value || emptyAddr();
  const set = (patch) => onChange({ ...a, ...patch });
  const c = lockCountry ? 'US' : (a.country || '');
  const regionLabel = c === 'US' ? 'State' : c === 'CA' ? 'Province' : c === 'GB' ? 'County (optional)' : 'State / province / region (optional)';
  const postalLabel = c === 'US' ? 'ZIP code' : c === 'GB' ? 'Postcode' : c === 'CA' ? 'Postal code' : 'Postal code (if any)';
  return (
    <>
      {title && <div className="pi-subhead pi-full">{title}</div>}
      {!lockCountry && (
        <Field label="Country">
          <Select k={k} value={c} onChange={(v) => set({ country: v, region: '' })} placeholder="Select country…"
            options={COUNTRIES.map((x) => [x, countryName(x)])} />
        </Field>
      )}
      <Field label="Address line 1" full={lockCountry}><Text k={k} value={a.line1} onChange={(v) => set({ line1: v })} autoComplete="address-line1" /></Field>
      <Field label="Address line 2 (optional)"><Text k={k} value={a.line2} onChange={(v) => set({ line2: v })} autoComplete="address-line2" /></Field>
      <Field label="City"><Text k={k} value={a.city} onChange={(v) => set({ city: v })} autoComplete="address-level2" /></Field>
      <Field label={regionLabel}>
        {c === 'US' || c === 'CA'
          ? <Select k={k} value={a.region} onChange={(v) => set({ region: v })} placeholder="Select…"
              options={(c === 'US' ? US_STATES : CA_PROVINCES).map((x) => [x, x])} />
          : <Text k={k} value={a.region} onChange={(v) => set({ region: v })} />}
      </Field>
      <Field label={postalLabel}><Text k={k} value={a.postal} onChange={(v) => set({ postal: v })} autoComplete="postal-code" /></Field>
    </>
  );
}

// A write-only secret + its confirmation. Blank = keep the value on file.
function SecretPair({ k, label, confirmLabel, masked, value, confirm, onValue, onConfirm, inputMode }) { // @@PAYMENT_INFO_W8_v105@@ +confirmLabel
  const ph = masked ? `On file ${masked} — leave blank to keep` : '';
  return (
    <>
      <Field label={label}>
        <Text k={k} value={value} onChange={onValue} placeholder={ph} autoComplete="off" inputMode={inputMode} spellCheck={false} />
      </Field>
      <Field label={confirmLabel || ('Confirm ' + (label === label.toUpperCase() ? label : label.toLowerCase()))}>
        <Text k={k} value={confirm} onChange={onConfirm} autoComplete="off" inputMode={inputMode} spellCheck={false}
          onPaste={(e) => e.preventDefault()} placeholder={value ? 'Type it again' : ''} />
      </Field>
    </>
  );
}

// ------------------------------------------------------------- W9 form ----
function w9Init(w) {
  w = w || {};
  return {
    legal_name: w.legal_name || '', business_name: w.business_name || '', tax_class: w.tax_class || '',
    llc_class: w.llc_class || '', tax_class_other: w.tax_class_other || '',
    exempt_payee_code: w.exempt_payee_code || '', fatca_code: w.fatca_code || '',
    address: w.address || emptyAddr('US'), tin_type: w.tin_type || 'ssn', tin: '', tin_confirm: '',
    certify: false, signature_name: '',
  };
}

function W9Form({ k, w9, onSave, busy, staff }) {
  const [f, setF] = useState(() => w9Init(w9));
  const [err, setErr] = useState('');
  useEffect(() => { setF(w9Init(w9)); }, [w9]);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const sameType = w9 && w9.tin_type === f.tin_type;

  function submit(e) {
    e.preventDefault(); setErr('');
    const tin = onlyDigits(f.tin);
    if (tin || !sameType) {
      if (!/^\d{9}$/.test(tin)) return setErr((f.tin_type === 'ssn' ? 'SSN' : 'EIN') + ' must be 9 digits.');
      if (onlyDigits(f.tin_confirm) !== tin) return setErr('Tax ID and its confirmation do not match.');
    }
    if (!staff && !f.certify) return setErr('Please check the certification box.');
    onSave({ w9: f }).then((m) => { if (m) setErr(m); else set({ tin: '', tin_confirm: '', certify: false, signature_name: '' }); });
  }

  return (
    <form className="pi-grid" onSubmit={submit} autoComplete="off">
      <Field label="Name (as shown on your income tax return)" full>
        <Text k={k} value={f.legal_name} onChange={(v) => set({ legal_name: v })} />
      </Field>
      <Field label="Business name / disregarded entity name (if different)" full>
        <Text k={k} value={f.business_name} onChange={(v) => set({ business_name: v })} />
      </Field>
      <Field label="Federal tax classification">
        <Select k={k} value={f.tax_class} onChange={(v) => set({ tax_class: v })} placeholder="Select…" options={TAX_CLASSES} />
      </Field>
      {f.tax_class === 'llc' && (
        <Field label="LLC taxed as">
          <Select k={k} value={f.llc_class} onChange={(v) => set({ llc_class: v })} placeholder="Select…"
            options={[['C', 'C corporation'], ['S', 'S corporation'], ['P', 'Partnership']]} />
        </Field>
      )}
      {f.tax_class === 'other' && (
        <Field label="Describe classification"><Text k={k} value={f.tax_class_other} onChange={(v) => set({ tax_class_other: v })} /></Field>
      )}
      <Field label="Exempt payee code (if any)"><Text k={k} value={f.exempt_payee_code} onChange={(v) => set({ exempt_payee_code: v })} maxLength={4} /></Field>
      <Field label="FATCA exemption code (if any)"><Text k={k} value={f.fatca_code} onChange={(v) => set({ fatca_code: v })} maxLength={4} /></Field>

      <AddressFields k={k} value={f.address} onChange={(a) => set({ address: a })} lockCountry title="Address (U.S.)" />

      <div className="pi-subhead pi-full">Taxpayer identification number</div>
      <Field label="Type">
        <Select k={k} value={f.tin_type} onChange={(v) => set({ tin_type: v, tin: '', tin_confirm: '' })}
          options={[['ssn', 'Social Security Number (SSN)'], ['ein', 'Employer Identification Number (EIN)']]} />
      </Field>
      <div className="pi-field" />
      <SecretPair k={k} label={f.tin_type === 'ssn' ? 'SSN' : 'EIN'} masked={sameType ? w9.tin_masked : null}
        value={f.tin} confirm={f.tin_confirm} inputMode="numeric"
        onValue={(v) => set({ tin: v })} onConfirm={(v) => set({ tin_confirm: v })} />

      {staff ? (
        <div className="pi-note pi-full">Entered by staff on the client's behalf — it is recorded as "Entered by staff (your email)", not as the client's signature. Keep the client's signed W9 on file.</div>
      ) : (
        <>
          <div className="pi-note pi-full">{W9_CERT}</div>
          <label className="pi-check pi-full">
            <input type="checkbox" checked={f.certify} onChange={(e) => set({ certify: e.target.checked })} />
            <span>I certify the above and sign this W9 electronically.</span>
          </label>
          <Field label="Signature (type your full name)">
            <Text k={k} value={f.signature_name} onChange={(v) => set({ signature_name: v })} />
          </Field>
          <Field label="Date"><Text k={k} value={new Date().toLocaleDateString()} onChange={() => {}} disabled /></Field>
        </>
      )}

      {err && <div className="pi-err pi-full">{err}</div>}
      <div className="pi-actions pi-full">
        <button className={k.btn} type="submit" disabled={busy}>{busy ? 'Saving…' : (w9 ? 'Update W9' : 'Save W9')}</button>
      </div>
    </form>
  );
}

// ------------------------------------------------------------- W-8 form ----
// @@PAYMENT_INFO_W8_v105@@ Non-U.S. payees (the client was marked international
// by an admin). Individual → W-8BEN, entity → W-8BEN-E. TINs are write-only.
const todayISO = () => new Date().toLocaleDateString('en-CA');
function w8Init(w) {
  w = w || {};
  return {
    form: w.form || '', name: w.name || '', country: w.country || '', disregarded_name: w.disregarded_name || '',
    ch3_status: w.ch3_status || '', ch4_status: w.ch4_status || '', ch4_status_other: w.ch4_status_other || '', giin: w.giin || '',
    address: w.address || emptyAddr(''), mailing_same: w.mailing_same !== false, mailing_address: w.mailing_address || emptyAddr(''),
    foreign_tin: '', foreign_tin_confirm: '', ftin_not_required: !!w.ftin_not_required, us_tin: '', us_tin_confirm: '',
    reference: w.reference || '', dob: w.dob || '',
    treaty_claim: !!w.treaty_claim, treaty_country: w.treaty_country || '', treaty_article: w.treaty_article || '',
    treaty_rate: w.treaty_rate == null ? '' : String(w.treaty_rate), treaty_income_type: w.treaty_income_type || '',
    treaty_conditions: w.treaty_conditions || '', lob: w.lob || '',
    capacity: w.entered_by_staff ? (w.capacity || '') : '', certify: false, signature_name: '',
    signed_on: w.entered_by_staff && w.signed_on ? w.signed_on : todayISO(),
  };
}

function W8Form({ k, w8, onSave, busy, staff }) {
  const [f, setF] = useState(() => w8Init(w8));
  const [err, setErr] = useState('');
  useEffect(() => { setF(w8Init(w8)); }, [w8]);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const ent = f.form === 'ben_e';
  const same = w8 && w8.form === f.form ? w8 : null;
  const norm = (s) => String(s || '').replace(/[\s-]/g, '').toUpperCase();
  const clearTins = { foreign_tin: '', foreign_tin_confirm: '', us_tin: '', us_tin_confirm: '' };

  function submit(e) {
    e.preventDefault(); setErr('');
    if (!f.form) return setErr('Choose who is getting paid: an individual (W-8BEN) or an entity (W-8BEN-E).');
    if (!f.ftin_not_required && norm(f.foreign_tin) && norm(f.foreign_tin) !== norm(f.foreign_tin_confirm)) return setErr('Foreign tax ID and its confirmation do not match.');
    if (norm(f.us_tin) && norm(f.us_tin) !== norm(f.us_tin_confirm)) return setErr('U.S. TIN and its confirmation do not match.');
    if (!staff && !f.certify) return setErr('Please check the certification box.');
    const payload = { ...f };
    if (!staff) delete payload.signed_on;
    onSave({ w8: payload }).then((m) => { if (m) setErr(m); else set({ ...clearTins, certify: false, signature_name: '' }); });
  }

  return (
    <form className="pi-grid" onSubmit={submit} autoComplete="off">
      <Field label="Who is getting paid?" full>
        <Select k={k} value={f.form} onChange={(v) => set({ form: v, ...clearTins })} placeholder="Select…" options={W8_FORMS} />
      </Field>
      {f.form && (
        <>
          <Field label={ent ? 'Name of organization' : 'Name of individual (the beneficial owner)'} full>
            <Text k={k} value={f.name} onChange={(v) => set({ name: v })} />
          </Field>
          <Field label={ent ? 'Country of incorporation or organization' : 'Country of citizenship'}>
            <Select k={k} value={f.country} onChange={(v) => set({ country: v })} placeholder="Select country…"
              options={NON_US.map((x) => [x, countryName(x)])} />
          </Field>
          {ent ? (
            <Field label="Disregarded entity receiving the payment (if any)">
              <Text k={k} value={f.disregarded_name} onChange={(v) => set({ disregarded_name: v })} />
            </Field>
          ) : (
            <Field label="Date of birth" hint="Required if you don't give a tax ID below.">
              <Text k={k} type="date" value={f.dob} onChange={(v) => set({ dob: v })} />
            </Field>
          )}
          {ent && (
            <>
              <Field label="Chapter 3 status (entity type)">
                <Select k={k} value={f.ch3_status} onChange={(v) => set({ ch3_status: v })} placeholder="Select…" options={CH3} />
              </Field>
              <Field label="Chapter 4 (FATCA) status" hint="Not sure? Ask your tax advisor.">
                <Select k={k} value={f.ch4_status} onChange={(v) => set({ ch4_status: v })} placeholder="Select…" options={CH4} />
              </Field>
              {f.ch4_status === 'other' && (
                <Field label="Describe the FATCA status" full>
                  <Text k={k} value={f.ch4_status_other} onChange={(v) => set({ ch4_status_other: v })} />
                </Field>
              )}
              <Field label={'GIIN' + (GIIN_CH4.includes(f.ch4_status) ? '' : ' (if any)')}>
                <Text k={k} value={f.giin} onChange={(v) => set({ giin: v.toUpperCase() })} placeholder="XXXXXX.XXXXX.XX.XXX" maxLength={19} spellCheck={false} />
              </Field>
              <div className="pi-field" />
            </>
          )}

          <AddressFields k={k} value={f.address} onChange={(a) => set({ address: a })}
            title="Permanent residence address (no P.O. box or in-care-of address)" />
          <label className="pi-check pi-full">
            <input type="checkbox" checked={f.mailing_same} onChange={(e) => set({ mailing_same: e.target.checked })} />
            <span>Mailing address is the same as above</span>
          </label>
          {!f.mailing_same && <AddressFields k={k} value={f.mailing_address} onChange={(a) => set({ mailing_address: a })} title="Mailing address" />}

          <div className="pi-subhead pi-full">Tax identification numbers</div>
          <label className="pi-check pi-full">
            <input type="checkbox" checked={f.ftin_not_required}
              onChange={(e) => set({ ftin_not_required: e.target.checked, foreign_tin: '', foreign_tin_confirm: '' })} />
            <span>{ent ? 'The entity’s' : 'My'} country doesn't issue a tax ID / a foreign TIN is not legally required</span>
          </label>
          {!f.ftin_not_required && (
            <SecretPair k={k} label="Foreign tax ID" confirmLabel="Confirm foreign tax ID" masked={same ? same.foreign_tin_masked : null}
              value={f.foreign_tin} confirm={f.foreign_tin_confirm}
              onValue={(v) => set({ foreign_tin: v })} onConfirm={(v) => set({ foreign_tin_confirm: v })} />
          )}
          <SecretPair k={k} label={'U.S. TIN (' + (ent ? 'EIN' : 'SSN or ITIN') + ', optional)'} confirmLabel="Confirm U.S. TIN"
            masked={same ? same.us_tin_masked : null} value={f.us_tin} confirm={f.us_tin_confirm} inputMode="numeric"
            onValue={(v) => set({ us_tin: v })} onConfirm={(v) => set({ us_tin_confirm: v })} />
          <Field label="Reference number (optional)"><Text k={k} value={f.reference} onChange={(v) => set({ reference: v })} /></Field>
          <div className="pi-field" />

          <div className="pi-subhead pi-full">Tax treaty benefits (optional)</div>
          <label className="pi-check pi-full">
            <input type="checkbox" checked={f.treaty_claim} onChange={(e) => set({ treaty_claim: e.target.checked })} />
            <span>{ent ? 'The entity claims' : 'I claim'} a reduced rate of U.S. withholding under an income tax treaty</span>
          </label>
          {f.treaty_claim && (
            <>
              <Field label="Treaty country (resident of)">
                <Select k={k} value={f.treaty_country} onChange={(v) => set({ treaty_country: v })} placeholder="Select country…"
                  options={NON_US.map((x) => [x, countryName(x)])} />
              </Field>
              <Field label="Treaty article and paragraph" hint="e.g. Article 12, paragraph 1">
                <Text k={k} value={f.treaty_article} onChange={(v) => set({ treaty_article: v })} maxLength={40} />
              </Field>
              <Field label="Withholding rate claimed (%)">
                <Text k={k} value={f.treaty_rate} onChange={(v) => set({ treaty_rate: v })} inputMode="decimal" maxLength={5} />
              </Field>
              <Field label="Type of income" hint="e.g. Royalties">
                <Text k={k} value={f.treaty_income_type} onChange={(v) => set({ treaty_income_type: v })} maxLength={100} />
              </Field>
              {ent && (
                <Field label="Limitation on benefits provision (if any)" full>
                  <Text k={k} value={f.lob} onChange={(v) => set({ lob: v })} maxLength={200} />
                </Field>
              )}
              <Field label="Additional conditions in the treaty article (optional)" full>
                <textarea className={k.input + ' pi-textarea'} value={f.treaty_conditions} onChange={(e) => set({ treaty_conditions: e.target.value })} maxLength={500} />
              </Field>
            </>
          )}

          {staff ? (
            <>
              <div className="pi-note pi-full">Entered by staff on the client's behalf — it is recorded as "Entered by staff (your email)", not as the client's signature. Keep the client's signed W-8 PDF on file.</div>
              <Field label="Date the client signed the paper form" hint="Sets the expiry (Dec 31 of the 3rd year after).">
                <Text k={k} type="date" value={f.signed_on} onChange={(v) => set({ signed_on: v })} />
              </Field>
              <Field label="Signer capacity (optional)"><Text k={k} value={f.capacity} onChange={(v) => set({ capacity: v })} /></Field>
            </>
          ) : (
            <>
              <div className="pi-note pi-full">{W8_CERT[f.form]}</div>
              <label className="pi-check pi-full">
                <input type="checkbox" checked={f.certify} onChange={(e) => set({ certify: e.target.checked })} />
                <span>I certify the above and sign this {W8_LABEL[f.form]} electronically.</span>
              </label>
              <Field label="Signature (type your full name)">
                <Text k={k} value={f.signature_name} onChange={(v) => set({ signature_name: v })} />
              </Field>
              <Field label={ent ? 'Capacity in which you are signing' : 'Capacity (only if signing for someone else)'}
                hint={ent ? 'e.g. Director, Manager, Authorized signatory' : undefined}>
                <Text k={k} value={f.capacity} onChange={(v) => set({ capacity: v })} />
              </Field>
              <Field label="Date"><Text k={k} value={new Date().toLocaleDateString()} onChange={() => {}} disabled /></Field>
            </>
          )}
        </>
      )}

      {err && <div className="pi-err pi-full">{err}</div>}
      {f.form && (
        <div className="pi-actions pi-full">
          <button className={k.btn} type="submit" disabled={busy}>{busy ? 'Saving…' : (w8 ? 'Update ' : 'Save ') + W8_LABEL[f.form]}</button>
        </div>
      )}
    </form>
  );
}

function w8RowList(w) {
  const ent = w.form === 'ben_e';
  const ch3 = (CH3.find((t) => t[0] === w.ch3_status) || [])[1];
  const ch4 = (CH4.find((t) => t[0] === w.ch4_status) || [])[1];
  return [
    ['Form', W8_LABEL[w.form] || w.form],
    ['Valid through', (w.expires_on || '—') + (w.expired ? ' (EXPIRED — client must re-sign)' : '')],
    [ent ? 'Organization' : 'Name', w.name],
    w.disregarded_name && ['Disregarded entity', w.disregarded_name],
    [ent ? 'Country of organization' : 'Citizenship', countryName(w.country)],
    ent && ['Chapter 3 status', ch3 || w.ch3_status],
    ent && ['FATCA status', (ch4 || w.ch4_status) + (w.ch4_status_other ? ' — ' + w.ch4_status_other : '')],
    w.giin && ['GIIN', w.giin],
    ['Residence address', addrLines(w.address)],
    !w.mailing_same && ['Mailing address', addrLines(w.mailing_address)],
    ['Foreign tax ID', w.ftin_not_required ? 'Not legally required' : secretVal(w.foreign_tin, w.foreign_tin_masked)],
    (w.us_tin || w.us_tin_masked) && ['U.S. TIN', secretVal(w.us_tin, w.us_tin_masked)],
    w.dob && ['Date of birth', w.dob],
    w.reference && ['Reference', w.reference],
    ['Treaty claim', w.treaty_claim
      ? [countryName(w.treaty_country), w.treaty_article, w.treaty_rate + '%', w.treaty_income_type].join(' · ') + (w.treaty_conditions ? '\n' + w.treaty_conditions : '')
      : 'None'],
    ent && w.lob && ['Limitation on benefits', w.lob],
    ['Signed', w.signature_name + (w.capacity ? ' (' + w.capacity + ')' : '') + ' · ' + (w.signed_on || fmtDate(w.signed_at))],
  ].filter(Boolean);
}

// Rows for the ACTIVE tax form (W9, or the W-8 when the client is international).
function taxRowList(info) {
  if (info.international) return info.w8 ? w8RowList(info.w8) : [];
  return info.w9 ? w9RowList(info.w9) : [];
}

function TaxFormSummary({ k, info, copy }) {
  const intl = !!info.international;
  const form = intl ? info.w8 : info.w9;
  const title = intl ? (info.w8 ? W8_LABEL[info.w8.form] : 'W-8') : 'W9';
  const rows = taxRowList(info);
  const lock = form && (intl && info.w8_expired ? '· expired — client can re-sign' : info.w9_locked ? '· locked' : '· unlocked for client editing');
  const other = intl ? info.w9 : info.w8; // the inactive form, if one is still stored
  return (
    <>
      <div className="pi-subhead pi-subhead-row">
        <span>{title} {lock && <span className="pi-lock">{lock}</span>}</span>
        {copy && form && <CopyBtn text={rowsText(title, rows)} label="Copy all" title={'Copy the whole ' + title + ' section'} />}
      </div>
      {form ? <Rows k={k} rows={rows} copy={copy} /> : <p className="pi-muted">No {intl ? 'W-8' : 'W9'} on file.</p>}
      {other && (
        <details className="pi-audit">
          <summary>{intl ? 'W9' : (W8_LABEL[other.form] || 'W-8')} also on file — inactive (client is {intl ? 'international' : 'domestic'})</summary>
          <Rows k={k} rows={intl ? w9RowList(other) : w8RowList(other)} copy={copy} />
        </details>
      )}
    </>
  );
}

// Portal section 1 for international clients.
function W8PortalSection({ k, info, clientId, demo, save, busy, saved }) {
  const w8 = info.w8;
  const label = w8 ? W8_LABEL[w8.form] : 'W-8';
  return (
    <>
      <div className={k.head}><h2>1. W-8 information</h2>{saved && <span className="pi-ok">W-8 saved ✓</span>}</div>
      <div className="pi-body">
        <p className="pi-muted">Your account is set up as international (non-U.S.), so instead of a W9 we need Form W-8BEN (individuals) or W-8BEN-E (companies and other entities). Tax IDs are encrypted and only shown masked after you save. Not sure about your FATCA or treaty status? Please ask your tax advisor.</p>
        {info.w9_locked
          ? <W9Locked k={k} w9={null} rows={w8RowList(w8)} formLabel="W-8" clientId={clientId} demo={demo} />
          : (
            <>
              {w8 && info.w8_expired && <div className="pi-note" style={{ marginBottom: 12 }}>Your {label} expired on {w8.expires_on}. Please review it and sign it again.</div>}
              {w8 && !info.w8_expired && <div className="pi-note" style={{ marginBottom: 12 }}>Your W-8 has been unlocked so you can update it. It locks again once you save.</div>}
              <W8Form k={k} w8={w8} onSave={save} busy={busy || demo} />
            </>
          )}
      </div>
    </>
  );
}

// ----------------------------------------------------- payout method form ----
function payInit(method, p, w9) {
  p = (p && p.method === method) ? p : {};
  const name = w9 ? w9.legal_name : '';
  const base = { method };
  if (method === 'check') return { ...base, mail_same_as_w9: p.mail_same_as_w9 !== false, mailing_address: p.mailing_address || emptyAddr('US') };
  if (method === 'ach') return {
    ...base, payee_name: p.payee_name || name, account_holder_type: p.account_holder_type || '', account_class: p.account_class || '',
    address: p.address || (w9 ? { ...w9.address } : emptyAddr('US')), email: p.email || '', bank_name: p.bank_name || '',
    routing: p.routing || '', routing_confirm: '', account_number: '', account_confirm: '',
  };
  if (method === 'wire') return {
    ...base, beneficiary_name: p.beneficiary_name || name, account_holder_type: p.account_holder_type || '',
    address: p.address || emptyAddr(''), email: p.email || '', bank_name: p.bank_name || '',
    bank_address: p.bank_address || emptyAddr(''), swift: p.swift || '', iban: '', iban_confirm: '',
    account_number: '', account_confirm: '', instructions: p.instructions || '', ack_fees: false,
    use_account: !!(p.account_number_masked && !p.iban_masked),
  };
  if (method === 'wise') return { ...base, wise_tag: p.wise_tag || '' };
  return { ...base, description: p.description || '' };
}

function PayoutForm({ k, info, onSave, busy }) {
  const w9 = info.w9;
  // @@PAYMENT_INFO_W8_v105@@ international: Wire / Wise / Other only; names prefill from the W-8.
  const intl = !!info.international;
  const nameSrc = intl ? (info.w8 ? { legal_name: info.w8.name } : null) : w9;
  const [method, setMethod] = useState(info.method || '');
  const [f, setF] = useState(() => (info.method ? payInit(info.method, info.payout, nameSrc) : null));
  const [err, setErr] = useState('');
  useEffect(() => { setMethod(info.method || ''); setF(info.method ? payInit(info.method, info.payout, nameSrc) : null); }, [info]); // nameSrc derives from info
  const set = (patch) => setF((p) => ({ ...p, ...patch }));
  const onFile = info.method === method ? info.payout || {} : {};

  function pick(m) { setErr(''); setMethod(m); setF(payInit(m, info.payout, nameSrc)); }
  const allowed = (m) => !intl || INTL_METHODS.includes(m);

  function submit(e) {
    e.preventDefault(); setErr('');
    if (method === 'ach') {
      const r = onlyDigits(f.routing);
      if (!abaValid(r)) return setErr('Routing number must be a valid 9-digit ABA routing number.');
      if (r !== (onFile.routing || '') && onlyDigits(f.routing_confirm) !== r) return setErr('Routing number and its confirmation do not match.');
      if ((f.account_number || !onFile.account_number_masked) && onlyDigits(f.account_confirm) !== onlyDigits(f.account_number)) {
        return setErr('Account number and its confirmation do not match.');
      }
    }
    if (method === 'wire') {
      if (!f.ack_fees) return setErr('Please acknowledge that wire fees are deducted from your payment.');
      const a = f.use_account ? 'account_number' : 'iban';
      const norm = (s) => String(s || '').replace(/[\s-]/g, '').toUpperCase();
      if (norm(f[a]) && norm(f[a]) !== norm(f[a === 'iban' ? 'iban_confirm' : 'account_confirm'])) {
        return setErr((a === 'iban' ? 'IBAN' : 'Account number') + ' and its confirmation do not match.');
      }
    }
    const payload = { ...f };
    if (method === 'wire') { if (f.use_account) { payload.iban = ''; } else { payload.account_number = ''; } delete payload.use_account; }
    onSave({ payout: payload }).then((m) => { if (m) setErr(m); });
  }

  const needsW9 = (m) => (m === 'check' || m === 'ach') && !w9;

  return (
    <div>
      <div className="pi-methods">
        {METHODS.filter((m) => allowed(m.id)).map((m) => (
          <button key={m.id} type="button" className={method === m.id ? k.pillOn : k.pill} onClick={() => pick(m.id)}>{m.label}</button>
        ))}
      </div>
      {!method && <p className="pi-muted">Choose how you would like to receive payment.</p>}
      {method && !allowed(method) && <div className="pi-note">{METHOD_LABEL[method]} isn't available for international payees — please choose Wire, Wise or Other.</div>}
      {method && needsW9(method) && <div className="pi-note">Please complete and save your W9 above first — {METHOD_LABEL[method]} payments are made to the name on your W9.</div>}
      {method && f && !needsW9(method) && allowed(method) && (
        <form className="pi-grid" onSubmit={submit} autoComplete="off">
          {method === 'check' && (
            <>
              <Field label="Pay to (from your W9)" full><Text k={k} value={w9.legal_name} onChange={() => {}} disabled /></Field>
              <label className="pi-check pi-full">
                <input type="checkbox" checked={f.mail_same_as_w9} onChange={(e) => set({ mail_same_as_w9: e.target.checked })} />
                <span>Mail checks to my W9 address</span>
              </label>
              {f.mail_same_as_w9
                ? <div className="pi-pre pi-full">{addrLines(w9.address)}</div>
                : <AddressFields k={k} value={f.mailing_address} onChange={(a) => set({ mailing_address: a })} title="Mailing address" />}
            </>
          )}

          {method === 'ach' && (
            <>
              <Field label="Payee name" full hint="Prefilled from your W9 — must match the name on the bank account.">
                <Text k={k} value={f.payee_name} onChange={(v) => set({ payee_name: v })} />
              </Field>
              <Field label="Account type">
                <Select k={k} value={f.account_holder_type} onChange={(v) => set({ account_holder_type: v })} placeholder="Select…"
                  options={[['business', 'Business'], ['personal', 'Personal']]} />
              </Field>
              <Field label="Checking or savings">
                <Select k={k} value={f.account_class} onChange={(v) => set({ account_class: v })} placeholder="Select…"
                  options={[['checking', 'Checking'], ['savings', 'Savings']]} />
              </Field>
              <AddressFields k={k} value={f.address} onChange={(a) => set({ address: a })} title="Payee address" />
              <Field label="Email (for payment notices)" full><Text k={k} type="email" value={f.email} onChange={(v) => set({ email: v })} /></Field>
              <div className="pi-subhead pi-full">Bank</div>
              <Field label="Bank name" full><Text k={k} value={f.bank_name} onChange={(v) => set({ bank_name: v })} /></Field>
              <Field label="Routing number (ABA)"><Text k={k} value={f.routing} onChange={(v) => set({ routing: v })} inputMode="numeric" maxLength={9} /></Field>
              <Field label="Confirm routing number">
                <Text k={k} value={f.routing_confirm} onChange={(v) => set({ routing_confirm: v })} inputMode="numeric" maxLength={9}
                  onPaste={(e) => e.preventDefault()} placeholder={onFile.routing && onlyDigits(f.routing) === onFile.routing ? 'Unchanged' : ''} />
              </Field>
              <SecretPair k={k} label="Account number" masked={onFile.account_number_masked} value={f.account_number} confirm={f.account_confirm}
                inputMode="numeric" onValue={(v) => set({ account_number: v })} onConfirm={(v) => set({ account_confirm: v })} />
            </>
          )}

          {method === 'wire' && (
            <>
              <div className="pi-note pi-full"><strong>Any wire transfer fees will be deducted from your payment.</strong> This includes our bank's fee and any intermediary or receiving-bank fees.</div>
              <Field label="Beneficiary name" full hint="Exactly as it appears on the bank account.">
                <Text k={k} value={f.beneficiary_name} onChange={(v) => set({ beneficiary_name: v })} />
              </Field>
              <Field label="Account type">
                <Select k={k} value={f.account_holder_type} onChange={(v) => set({ account_holder_type: v })} placeholder="Select…"
                  options={[['business', 'Business'], ['personal', 'Personal']]} />
              </Field>
              <Field label="Email (for payment notices)"><Text k={k} type="email" value={f.email} onChange={(v) => set({ email: v })} /></Field>
              <AddressFields k={k} value={f.address} onChange={(a) => set({ address: a })} title="Beneficiary address" />
              <div className="pi-subhead pi-full">Bank</div>
              <Field label="Bank name"><Text k={k} value={f.bank_name} onChange={(v) => set({ bank_name: v })} /></Field>
              <Field label="SWIFT / BIC"><Text k={k} value={f.swift} onChange={(v) => set({ swift: v.toUpperCase() })} maxLength={11} spellCheck={false} /></Field>
              <AddressFields k={k} value={f.bank_address} onChange={(a) => set({ bank_address: a })} title="Bank address" />
              <label className="pi-check pi-full">
                <input type="checkbox" checked={f.use_account} onChange={(e) => set({ use_account: e.target.checked, iban: '', iban_confirm: '', account_number: '', account_confirm: '' })} />
                <span>My bank's country doesn't use IBAN — I'll enter an account number instead</span>
              </label>
              {f.use_account
                ? <SecretPair k={k} label="Account number" masked={onFile.account_number_masked} value={f.account_number} confirm={f.account_confirm}
                    onValue={(v) => set({ account_number: v })} onConfirm={(v) => set({ account_confirm: v })} />
                : <SecretPair k={k} label="IBAN" masked={onFile.iban_masked} value={f.iban} confirm={f.iban_confirm}
                    onValue={(v) => set({ iban: v })} onConfirm={(v) => set({ iban_confirm: v })} />}
              <Field label="Additional bank instructions (optional)" full hint="Intermediary bank, branch / transit / BSB / IFSC code, etc.">
                <textarea className={k.input + ' pi-textarea'} value={f.instructions} onChange={(e) => set({ instructions: e.target.value })} maxLength={500} />
              </Field>
              <label className="pi-check pi-full">
                <input type="checkbox" checked={f.ack_fees} onChange={(e) => set({ ack_fees: e.target.checked })} />
                <span>I understand wire fees are deducted from my payment.</span>
              </label>
            </>
          )}

          {method === 'wise' && (
            <>
              <div className="pi-note pi-full"><strong>All payments are paid in USD.</strong></div>
              <Field label="Wise @tag or Wise account email" full>
                <Text k={k} value={f.wise_tag} onChange={(v) => set({ wise_tag: v })} placeholder="@yourname" spellCheck={false} />
              </Field>
            </>
          )}

          {method === 'other' && (
            <>
              <div className="pi-note pi-full">
                Please describe how you would like to get paid. <strong>Cash is not an option.</strong> We treat this as a request and will do our best to accommodate. <strong>We do not use Venmo or Zelle.</strong>
              </div>
              <Field label="How would you like to get paid?" full>
                <textarea className={k.input + ' pi-textarea'} value={f.description} onChange={(e) => set({ description: e.target.value })} maxLength={1000} />
              </Field>
            </>
          )}

          {err && <div className="pi-err pi-full">{err}</div>}
          <div className="pi-actions pi-full">
            <button className={k.btn} type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save payment method'}</button>
          </div>
        </form>
      )}
    </div>
  );
}

// ------------------------------------------------------- read-only view ----
async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) { /* fall back below */ }
  const ta = document.createElement('textarea');
  ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
  document.body.appendChild(ta); ta.select();
  let ok = false; try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
  ta.remove(); return ok;
}
const isMasked = (v) => typeof v === 'string' && v.includes('••••');
const hasValue = (v) => v != null && v !== '';

function CopyBtn({ text, label, title }) {
  const [state, setState] = useState('');
  useEffect(() => { if (!state) return undefined; const t = setTimeout(() => setState(''), 1500); return () => clearTimeout(t); }, [state]);
  return (
    <button type="button" className={'pi-copy' + (state ? ' pi-copy-' + state : '')} title={title || 'Copy'}
      onClick={async () => setState((await copyText(String(text))) ? 'ok' : 'fail')}>
      {state === 'ok' ? 'Copied' : state === 'fail' ? 'Copy failed' : (label || 'Copy')}
    </button>
  );
}

// Plain-text block for "Copy all": "Label: value" lines (multi-line values indented).
function rowsText(title, rows) {
  const lines = rows.filter(Boolean).map(([l, v]) => {
    const val = hasValue(v) ? String(v) : '—';
    return l + ': ' + val.split('\n').join('\n  ');
  });
  return [title, ...lines].join('\n');
}

function Rows({ k, rows, copy }) {
  return (
    <table className={k.table + ' pi-kv'}>
      <tbody>
        {rows.filter(Boolean).map(([l, v]) => (
          <tr key={l}>
            <td className="pi-kv-l">{l}</td>
            <td className="pi-pre">{hasValue(v) ? v : '—'}</td>
            {copy && (
              <td className="pi-kv-c">
                {hasValue(v) && !isMasked(v) && <CopyBtn text={v} title={'Copy ' + l} />}
                {isMasked(v) && <span className="pi-hint" title="Reveal to copy the full number">masked</span>}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
const secretVal = (full, masked) => full || masked || null;
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function w9RowList(w) {
  const tc = (TAX_CLASSES.find((t) => t[0] === w.tax_class) || [])[1];
  return [
    ['Name', w.legal_name], w.business_name && ['Business name', w.business_name],
    ['Tax classification', (tc || w.tax_class) + (w.llc_class ? ' (' + w.llc_class + ')' : '') + (w.tax_class_other ? ' — ' + w.tax_class_other : '')],
    (w.exempt_payee_code || w.fatca_code) && ['Exemptions', [w.exempt_payee_code && 'payee ' + w.exempt_payee_code, w.fatca_code && 'FATCA ' + w.fatca_code].filter(Boolean).join(', ')],
    ['Address', addrLines(w.address)],
    [w.tin_type === 'ein' ? 'EIN' : 'SSN', secretVal(w.tin, w.tin_masked)],
    ['Signed', w.signature_name + ' · ' + fmtDate(w.signed_at)],
  ].filter(Boolean);
}

function W9Rows({ k, w, copy }) {
  return <Rows k={k} rows={w9RowList(w)} copy={copy} />;
}

function Summary({ k, info, copy }) {
  const p = info.payout;
  const w9List = taxRowList(info); // @@PAYMENT_INFO_W8_v105@@ active tax form (W9 or W-8)
  const payList = p ? payoutRowList(info, p) : [];
  const anyMasked = [...w9List, ...payList].some(([, v]) => isMasked(v));
  return (
    <div className="pi-summary">
      <TaxFormSummary k={k} info={info} copy={copy} />
      <div className="pi-subhead pi-subhead-row">
        <span>Payment method</span>
        {copy && p && <CopyBtn text={rowsText('Payment method', payList)} label="Copy all" title="Copy the whole payment section" />}
      </div>
      {p ? <Rows k={k} rows={payList} copy={copy} /> : <p className="pi-muted">No payment method chosen.</p>}
      {copy && anyMasked && <p className="pi-muted">Masked numbers copy as ••••. Use Reveal to copy the full numbers (logged).</p>}
    </div>
  );
}

function payoutRowList(info, p) {
  return ([
    ['Method', METHOD_LABEL[info.method]],
    ...(info.method === 'check' ? [['Pay to', p.pay_to], ['Mail to', addrLines(p.mailing_address_resolved) + (p.mail_same_as_w9 ? '\n(W9 address)' : '')]] : []),
    ...(info.method === 'ach' ? [
      ['Payee', p.payee_name], ['Account', `${cap(p.account_holder_type)} · ${cap(p.account_class)}`], ['Address', addrLines(p.address)],
      ['Email', p.email], ['Bank', p.bank_name], ['Routing', p.routing], ['Account number', secretVal(p.account_number, p.account_number_masked)],
    ] : []),
    ...(info.method === 'wire' ? [
      ['Beneficiary', p.beneficiary_name], ['Account type', cap(p.account_holder_type)], ['Address', addrLines(p.address)], ['Email', p.email],
      ['Bank', p.bank_name], ['Bank address', addrLines(p.bank_address)], ['SWIFT / BIC', p.swift],
      p.iban_masked ? ['IBAN', secretVal(p.iban, p.iban_masked)] : ['Account number', secretVal(p.account_number, p.account_number_masked)],
      p.instructions && ['Instructions', p.instructions], ['Wire fees', 'Deducted from payment (acknowledged)'],
    ] : []),
    ...(info.method === 'wise' ? [['Wise', p.wise_tag], ['Currency', 'USD']] : []),
    ...(info.method === 'other' ? [['Request', p.description]] : []),
  ]).filter(Boolean);
}

// A saved W9 is read-only for the client; they ask us to unlock it.
function W9Locked({ k, w9, clientId, demo, formLabel, rows }) { // @@PAYMENT_INFO_W8_v105@@ also used for the W-8
  const fl = formLabel || 'W9';
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  async function send() {
    setBusy(true); setMsg('');
    try {
      const res = await apiFetch('/api/portal/payment-info/w9-change-request', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, note }),
      });
      const j = await jsonOf(res);
      if (!res.ok) { setMsg(j.error || 'Could not send your request.'); return; }
      setOpen(false); setNote('');
      setMsg(j.existing ? 'You already have a change request open — our team will unlock your ' + fl + ' shortly.' : 'Request sent. We’ll unlock your ' + fl + ' for editing and let you know.');
    } finally { setBusy(false); }
  }
  return (
    <>
      <div className="pi-note">Your {fl} is on file and locked. If something needs to change, please contact us — we'll unlock it so you can update it.</div>
      {rows ? <Rows k={k} rows={rows} /> : <W9Rows k={k} w={w9} />}
      {msg && <p className="pi-muted"><strong>{msg}</strong></p>}
      {open ? (
        <div className="pi-grid">
          <Field label="What needs to change? (optional — don't include your tax ID here)" full>
            <textarea className={k.input + ' pi-textarea'} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          </Field>
          <div className="pi-actions pi-full">
            <button type="button" className={k.pill} onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className={k.btn} disabled={busy} onClick={send}>{busy ? 'Sending…' : 'Send request'}</button>
          </div>
        </div>
      ) : (
        <div className="pi-actions">
          <button type="button" className={k.btn} disabled={demo} onClick={() => setOpen(true)}>Request a {fl} change</button>
        </div>
      )}
    </>
  );
}

// Dashboard reminder: shows only while the W9 / payment method is missing.
export function PaymentInfoReminder({ clientId, onGo }) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    let live = true;
    setSt(null);
    if (!clientId) return undefined;
    apiFetch('/api/portal/payment-info/status?client_id=' + encodeURIComponent(clientId))
      .then((r) => (r.ok ? r.json() : null)).then((j) => { if (live) setSt(j); }).catch(() => {});
    return () => { live = false; };
  }, [clientId]);
  if (!st || st.complete || st.not_applicable) return null; // @@PAYMENT_ACCESS_v125@@
  return (
    <div className="pi-reminder">
      <div>
        <div className="pi-reminder-title">Action needed: add your {st.missing.join(' and ')}</div>
        <div className="pi-reminder-sub">We need this on file before we can send you a payout.</div>
      </div>
      {onGo && <button className="cp-btn-sm" onClick={onGo}>Add payment info</button>}
    </div>
  );
}

// ------------------------------------------------------ client portal ----
export function PaymentInfoPortalTab({ clientId }) {
  const k = CLS.portal;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');
  const url = '/api/portal/payment-info' + (clientId ? '?client_id=' + encodeURIComponent(clientId) : '');

  const load = useCallback(async () => {
    setErr(''); setData(null);
    const res = await apiFetch(url);
    const j = await jsonOf(res);
    if (!res.ok) { setErr(j.error || 'Could not load payment info.'); return; }
    setData(j);
  }, [url]);
  useEffect(() => { load(); }, [load]);

  async function save(body) {
    setBusy(true); setSaved('');
    try {
      const res = await apiFetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await jsonOf(res);
      if (!res.ok) return j.error || 'Could not save.';
      setData((d) => ({ ...d, info: j.info }));
      setSaved(body.w9 ? 'w9' : body.w8 ? 'w8' : 'payout'); // @@PAYMENT_INFO_W8_v105@@
      return '';
    } finally { setBusy(false); }
  }

  if (err) return <div className="cp-card"><div className="cp-empty">{err}</div></div>;
  if (!data) return <div className="cp-card"><div className="cp-empty">Loading…</div></div>;
  return (
    <div className="pi-root">
      {data.demo && <div className="cp-card"><div className="cp-empty">Demo account — this form is view-only.</div></div>}
      <div className={k.card}>
        {data.info.international ? ( // @@PAYMENT_INFO_W8_v105@@ international → W-8 instead of W9
          <W8PortalSection k={k} info={data.info} clientId={clientId} demo={!!data.demo} save={save} busy={busy} saved={saved === 'w8'} />
        ) : (
          <>
            <div className={k.head}><h2>1. W9 information</h2>{saved === 'w9' && <span className="pi-ok">W9 saved ✓</span>}</div>
            <div className="pi-body">
              <p className="pi-muted">Required for U.S. payees. Your tax ID is encrypted and only shown masked after you save. Not a U.S. person? Please contact us and we'll switch your account to a W-8 form.</p>
              {data.info.w9_locked
                ? <W9Locked k={k} w9={data.info.w9} clientId={clientId} demo={!!data.demo} />
                : (
                  <>
                    {data.info.w9 && <div className="pi-note" style={{ marginBottom: 12 }}>Your W9 has been unlocked so you can update it. It locks again once you save.</div>}
                    <W9Form k={k} w9={data.info.w9} onSave={save} busy={busy || !!data.demo} />
                  </>
                )}
            </div>
          </>
        )}
      </div>
      <div className={k.card}>
        <div className={k.head}><h2>2. How would you like to get paid?</h2>{saved === 'payout' && <span className="pi-ok">Payment method saved ✓</span>}</div>
        <div className="pi-body"><PayoutForm k={k} info={data.info} onSave={save} busy={busy || !!data.demo} /></div>
      </div>
      {data.info.updated_at && <p className="pi-muted">Last updated {fmtDate(data.info.updated_at)}. Bank details are encrypted and shown masked.</p>}
    </div>
  );
}

// ------------------------------------------------------ staff panel ----
export function PaymentInfoStaffPanel({ clientId }) {
  const k = CLS.staff;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const base = '/api/clients/' + clientId + '/payment-info';

  const load = useCallback(async () => {
    setErr('');
    const res = await apiFetch(base);
    const j = await jsonOf(res);
    if (!res.ok) { setErr(j.error || 'Could not load payment info.'); return; }
    setData(j);
  }, [base]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { // auto re-mask after 60s
    if (!revealed) return undefined;
    const t = setTimeout(() => setRevealed(null), 60000);
    return () => clearTimeout(t);
  }, [revealed]);

  async function toggleLock() {
    const unlocking = data.info.w9_locked;
    const fl = data.info.international ? 'W-8' : 'W9'; // @@PAYMENT_INFO_W8_v105@@
    if (unlocking && !window.confirm('Unlock this ' + fl + ' so the client can edit it? It re-locks when they save.')) return;
    const res = await apiFetch(base + '/w9-lock', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ locked: !unlocking }),
    });
    const j = await jsonOf(res);
    if (!res.ok) { setErr(j.error || 'Could not change the W9 lock.'); return; }
    load();
  }

  // @@PAYMENT_INFO_W8_v105@@ admin: international (W-8) ↔ domestic (W9).
  async function toggleIntl() {
    const next = !data.info.international;
    if (!window.confirm(next
      ? 'Mark this client as international? They will be asked for a W-8 (W-8BEN / W-8BEN-E) instead of a W9, and payouts are limited to Wire, Wise or Other.'
      : 'Mark this client as domestic (U.S.)? They will be asked for a W9 instead of a W-8.')) return;
    const res = await apiFetch(base + '/international', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ international: next }),
    });
    const j = await jsonOf(res);
    if (!res.ok) { setErr(j.error || 'Could not change the tax status.'); return; }
    setRevealed(null); load();
  }

  // @@PAYMENT_ACCESS_v125@@ admin: does the CLIENT PORTAL ask this client for Payment Info? (Automatic = follows the linked Shopify store)
  async function setAccess(v) {
    const mode = v === 'auto' ? null : v;
    if (mode === 'not_needed' && !window.confirm('Mark Payment info as not needed? The client will no longer see the Payment Info tab or the reminder, and payout requests will not be held for it.')) return;
    const res = await apiFetch(base + '/access', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode }),
    });
    const j = await jsonOf(res);
    if (!res.ok) { setErr(j.error || 'Could not change the Payment info setting.'); return; }
    load();
  }

  async function reveal() {
    if (!window.confirm('Show full tax ID and bank numbers? This is logged.')) return;
    const res = await apiFetch(base + '/reveal', { method: 'POST' });
    const j = await jsonOf(res);
    if (!res.ok) { setErr(j.error || 'Reveal failed.'); return; }
    setRevealed(j.info); load();
  }

  async function save(body) {
    setBusy(true);
    try {
      const res = await apiFetch(base, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await jsonOf(res);
      if (!res.ok) return j.error || 'Could not save.';
      setRevealed(null); await load();
      return '';
    } finally { setBusy(false); }
  }

  return (
    <div className={k.card + ' pi-root'} style={{ marginBottom: 20 }}>
      <div className={k.head}>
        <h2>Payment info</h2>
        {data && (
          <div className="pi-headbtns">
            {data.can_reveal && (data.info.w9 || data.info.w8 || data.info.payout) && (revealed
              ? <button className="hq-btn hq-btn-ghost" onClick={() => setRevealed(null)}>Hide</button>
              : <button className="hq-btn hq-btn-ghost" onClick={reveal}>Reveal</button>)}
            {data.can_edit && (
              <button className="hq-btn hq-btn-ghost" onClick={toggleIntl}>{data.info.international ? 'Mark domestic (W9)' : 'Mark international (W-8)'}</button>
            )}
            {data.can_edit && (data.info.international ? data.info.w8 : data.info.w9) && (
              <button className="hq-btn hq-btn-ghost" onClick={toggleLock}>{(data.info.w9_locked ? 'Unlock ' : 'Lock ') + (data.info.international ? 'W-8' : 'W9')}</button>
            )}
            {data.can_edit && <button className="hq-btn" onClick={() => setEditing((e) => !e)}>{editing ? 'Done' : 'Edit'}</button>}
          </div>
        )}
      </div>
      <div className="pi-body">
        {err && <p className="pi-err">{err}</p>}
        {!data && !err && <p className="pi-muted">Loading…</p>}
        {data && (
          <>
            {revealed && <p className="pi-warn">Full numbers shown — hides automatically in 60 seconds. This view was logged.</p>}
            <p className="pi-muted">Tax status: <strong>{data.info.international ? 'International — W-8' : 'Domestic (U.S.) — W9'}</strong>{data.info.international ? ' · payouts by Wire, Wise or Other' : ''}</p>
            {data.access && (
              <p className="pi-muted pi-access">{/* @@PAYMENT_ACCESS_v125@@ */}
                Client portal asks for Payment info:{' '}
                {data.can_edit ? (
                  <select className={k.input} style={{ width: 'auto' }} value={data.access.mode || 'auto'} onChange={(e) => setAccess(e.target.value)}>
                    <option value="auto">{'Automatic — ' + (data.access.has_store ? 'has a linked store' : 'no linked store')}</option>
                    <option value="required">Required</option>
                    <option value="not_needed">Not needed</option>
                  </select>
                ) : <strong>{data.access.mode === 'required' ? 'Required' : data.access.mode === 'not_needed' ? 'Not needed' : 'Automatic'}</strong>}
                {' '}<span>· {data.access.needed ? 'currently shown to the client' : 'currently hidden from the client'}</span>
              </p>
            )}
            <Summary k={k} info={revealed || data.info} copy />
            {data.info.updated_at && <p className="pi-muted">Last updated {fmtDate(data.info.updated_at)} {data.info.updated_via === 'portal' ? 'by the client (portal)' : 'by staff'}.</p>}
            {editing && data.can_edit && (
              <div className="pi-edit">
                {data.info.international ? (
                  <>
                    <div className="pi-subhead">Edit W-8 (admin)</div>
                    <W8Form k={k} w8={data.info.w8} onSave={save} busy={busy} staff />
                  </>
                ) : (
                  <>
                    <div className="pi-subhead">Edit W9 (admin)</div>
                    <W9Form k={k} w9={data.info.w9} onSave={save} busy={busy} staff />
                  </>
                )}
                <div className="pi-subhead">Edit payment method (admin)</div>
                <PayoutForm k={k} info={data.info} onSave={save} busy={busy} />
              </div>
            )}
            {data.audit && data.audit.length > 0 && (
              <details className="pi-audit">
                <summary>Activity ({data.audit.length})</summary>
                <table className="hq-table">
                  <tbody>
                    {data.audit.map((a) => (
                      <tr key={a.id}>
                        <td>{fmtDate(a.created_at)}</td>
                        <td>{a.user_email || '—'}</td>
                        <td>{AUDIT_LABEL[a.action] || a.action}{a.via === 'portal' ? ' (portal)' : ''}</td>
                        <td>{a.detail || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}
